import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MainLayout } from '@/layouts/MainLayout';
import { Container, Stack, Button, Alert, Skeleton, PageHeader } from '@/components/ui';
import { UserForm, UserTable, UserFilters, ConfirmationDialog } from '@/components/userManagement';
import { UserManagementService } from '@/services/userManagement.service';
import { useToast } from '@/contexts/ToastContext';
import { getApiErrorMessage } from '@/utils/apiError';
import type {
  User,
  Company,
  UserGroupInfo,
  UserFilter,
  CreateUserRequest,
  UpdateUserRequest,
  UserListResponse,
} from '@/types/userManagement.types';

type SortField = 'id' | 'name' | 'email' | 'status' | 'companies' | 'groups' | 'lastLoginAt';
type SortDirection = 'asc' | 'desc';

/**
 * Client-side sorting utility function
 */
function sortUsers(users: User[], sortField: SortField, sortDirection: SortDirection): User[] {
  const dir = sortDirection === 'asc' ? 1 : -1;
  const locale: Intl.LocalesArgument = undefined;
  const collate: Intl.CollatorOptions = { sensitivity: 'base', numeric: true };

  return [...users].sort((a, b) => {
    let cmp = 0;

    switch (sortField) {
      case 'id':
        cmp = a.id - b.id;
        break;
      case 'name':
        cmp = a.name.localeCompare(b.name, locale, collate);
        break;
      case 'email':
        cmp = a.email.localeCompare(b.email, locale, collate);
        break;
      case 'status':
        cmp = a.inviteStatus.localeCompare(b.inviteStatus, locale, collate);
        break;
      case 'companies': {
        const aName = a.companies[0]?.name ?? null;
        const bName = b.companies[0]?.name ?? null;
        if (aName === null && bName === null) break;
        if (aName === null) return 1;
        if (bName === null) return -1;
        cmp = aName.localeCompare(bName, locale, collate);
        break;
      }
      case 'groups': {
        const aRoles = [...(a.userGroups ?? []), ...(a.companyUserGroups ?? [])];
        const bRoles = [...(b.userGroups ?? []), ...(b.companyUserGroups ?? [])];
        const aName = aRoles[0]?.name ?? null;
        const bName = bRoles[0]?.name ?? null;
        if (aName === null && bName === null) break;
        if (aName === null) return 1;
        if (bName === null) return -1;
        cmp = aName.localeCompare(bName, locale, collate);
        break;
      }
      case 'lastLoginAt': {
        const aTime = a.lastLoginAt ? new Date(a.lastLoginAt).getTime() : null;
        const bTime = b.lastLoginAt ? new Date(b.lastLoginAt).getTime() : null;
        if (aTime === null && bTime === null) break;
        if (aTime === null) return 1;
        if (bTime === null) return -1;
        cmp = aTime - bTime;
        break;
      }
      default:
        return 0;
    }

    if (cmp !== 0) return cmp * dir;
    // Secondary: stable alphabetical by name
    return a.name.localeCompare(b.name, locale, { sensitivity: 'base' });
  });
}

/**
 * Client-side filtering utility function
 * Filters users based on the provided filter criteria
 */
function filterUsers(users: User[], filters: UserFilter): User[] {
  return users.filter((user) => {
    // Search filter - searches in name, email, and whatsapp number
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      const matchesSearch =
        user.name.toLowerCase().includes(searchTerm) ||
        user.email.toLowerCase().includes(searchTerm) ||
        (user.whatsAppNumber?.toLowerCase().includes(searchTerm) ?? false);

      if (!matchesSearch) return false;
    }

    // Company filter
    if (filters.companyId) {
      const hasCompany = user.companies.some((company) => company.id === filters.companyId);
      if (!hasCompany) return false;
    }

    // User group filter
    if (filters.userGroupId) {
      const hasUserGroup = user.userGroups.some((group) => group.id === filters.userGroupId);
      if (!hasUserGroup) return false;
    }

    // Invite status filter
    if (filters.inviteStatus) {
      if (user.inviteStatus !== filters.inviteStatus) return false;
    }

    // Notification enabled filter
    if (filters.notificationEnabled !== undefined) {
      const hasNotifications =
        user.isNotifyMonday ||
        user.isNotifyTuesday ||
        user.isNotifyWednesday ||
        user.isNotifyThursday ||
        user.isNotifyFriday;

      if (filters.notificationEnabled && !hasNotifications) return false;
      if (!filters.notificationEnabled && hasNotifications) return false;
    }

    return true;
  });
}

interface UserManagementPageState {
  allUsers: User[];
  companies: Company[];
  userGroups: UserGroupInfo[];
  isUsersLoading: boolean;
  isMetadataLoading: boolean;
  error: string | null;
  filters: UserFilter;
  currentPage: number;
  pageSize: number;
  sortField: SortField;
  sortDirection: SortDirection;
}

interface DialogState {
  showForm: boolean;
  showDeleteConfirm: boolean;
  editingUser: User | null;
  deletingUser: User | null;
  formLoading: boolean;
}

export const UserManagementPage: React.FC = () => {
  const { showToast } = useToast();
  const [state, setState] = useState<UserManagementPageState>({
    allUsers: [],
    companies: [],
    userGroups: [],
    isUsersLoading: true,
    isMetadataLoading: true,
    error: null,
    filters: {},
    currentPage: 1,
    pageSize: 25,
    sortField: 'id',
    sortDirection: 'desc',
  });

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const activeFilterCount = useMemo(() => {
    return Object.values(state.filters).filter((v) => v !== undefined && v !== '' && v !== null)
      .length;
  }, [state.filters]);

  const [dialogs, setDialogs] = useState<DialogState>({
    showForm: false,
    showDeleteConfirm: false,
    editingUser: null,
    deletingUser: null,
    formLoading: false,
  });

  // Load all users from API (no filtering on backend)
  const loadUsers = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isUsersLoading: true, error: null }));

      // Fetch all users without filters (backend doesn't support filtering)
      const response: UserListResponse = await UserManagementService.getUsers();

      setState((prev) => ({
        ...prev,
        allUsers: response.users,
        isUsersLoading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load users',
        isUsersLoading: false,
      }));
    }
  }, []);

  // Load initial data (companies and user groups) - runs once on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setState((prev) => ({ ...prev, isMetadataLoading: true, error: null }));

        const [companiesData, userGroupsData] = await Promise.all([
          UserManagementService.getCompanies(),
          UserManagementService.getUserGroups(),
        ]);

        setState((prev) => ({
          ...prev,
          companies: companiesData,
          userGroups: userGroupsData,
          isMetadataLoading: false,
        }));
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Failed to load data',
          isMetadataLoading: false,
        }));
      }
    };

    loadInitialData();
  }, []); // Empty deps - only run once on mount

  // Load users once on mount
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Apply client-side filtering and sorting
  const filteredUsers = useMemo(() => {
    const filtered = filterUsers(state.allUsers, state.filters);
    return sortUsers(filtered, state.sortField, state.sortDirection);
  }, [state.allUsers, state.filters, state.sortField, state.sortDirection]);

  const paginatedUsers = useMemo(() => {
    const startIndex = (state.currentPage - 1) * state.pageSize;
    const endIndex = startIndex + state.pageSize;
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, state.currentPage, state.pageSize]);

  const totalPages = Math.ceil(filteredUsers.length / state.pageSize);
  const totalCount = filteredUsers.length;

  const paginationItems = useMemo<(number | string)[]>(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const items: (number | string)[] = [1];
    const start = Math.max(2, state.currentPage - 1);
    const end = Math.min(totalPages - 1, state.currentPage + 1);

    if (start > 2) items.push('left-ellipsis');
    for (let page = start; page <= end; page += 1) items.push(page);
    if (end < totalPages - 1) items.push('right-ellipsis');

    items.push(totalPages);
    return items;
  }, [state.currentPage, totalPages]);

  const handleFiltersChange = useCallback((newFilters: UserFilter) => {
    setState((prev) => ({
      ...prev,
      filters: newFilters,
      currentPage: 1, // Reset to first page when filters change
    }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setState((prev) => ({
      ...prev,
      filters: {},
      currentPage: 1,
    }));
  }, []);

  const handleSort = (field: SortField) => {
    setState((prev) => ({
      ...prev,
      sortField: field,
      sortDirection: prev.sortField === field && prev.sortDirection === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handlePageChange = (page: number) => {
    setState((prev) => ({ ...prev, currentPage: page }));
  };

  // Form handlers
  const handleCreateUser = () => {
    setDialogs((prev) => ({
      ...prev,
      showForm: true,
      editingUser: null,
    }));
  };

  const handleEditUser = (user: User) => {
    setDialogs((prev) => ({
      ...prev,
      showForm: true,
      editingUser: user,
    }));
  };

  const handleFormSubmit = async (data: CreateUserRequest | UpdateUserRequest) => {
    try {
      setDialogs((prev) => ({ ...prev, formLoading: true }));

      if (dialogs.editingUser) {
        await UserManagementService.updateUser(dialogs.editingUser.id, data as UpdateUserRequest);
        showToast('User updated successfully', { variant: 'success' });
      } else {
        await UserManagementService.createUser(data as CreateUserRequest);
        showToast('User created successfully', { variant: 'success' });
      }

      await loadUsers();
      setDialogs((prev) => ({
        ...prev,
        showForm: false,
        editingUser: null,
        formLoading: false,
      }));
    } catch (error) {
      setDialogs((prev) => ({ ...prev, formLoading: false }));
      showToast(getApiErrorMessage(error, 'Failed to save user'), {
        variant: 'error',
      });
      // Rethrown so the form can mark the offending field and keep the values for a retry.
      throw error;
    }
  };

  const handleFormCancel = () => {
    setDialogs((prev) => ({
      ...prev,
      showForm: false,
      editingUser: null,
      formLoading: false,
    }));
  };

  // Delete handlers
  const handleDeleteUser = (user: User) => {
    setDialogs((prev) => ({
      ...prev,
      showDeleteConfirm: true,
      deletingUser: user,
    }));
  };

  const handleConfirmDelete = async () => {
    if (!dialogs.deletingUser) return;

    try {
      setDialogs((prev) => ({ ...prev, formLoading: true }));
      await UserManagementService.deleteUser(dialogs.deletingUser.id);
      await loadUsers();
      showToast('User deleted successfully', { variant: 'success' });
      setDialogs((prev) => ({
        ...prev,
        showDeleteConfirm: false,
        deletingUser: null,
        formLoading: false,
      }));
    } catch (error) {
      setDialogs((prev) => ({ ...prev, formLoading: false }));
      showToast(error instanceof Error ? error.message : 'Failed to delete user', {
        variant: 'error',
      });
    }
  };

  // Resend invitation handler
  const handleResendInvite = async (user: User) => {
    try {
      await UserManagementService.resendInvitation({ userId: user.id });
      showToast('Invitation resent successfully', { variant: 'success' });
      await loadUsers();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to resend invitation', {
        variant: 'error',
      });
    }
  };

  const handleCancelDialog = () => {
    setDialogs((prev) => ({
      ...prev,
      showDeleteConfirm: false,
      deletingUser: null,
      formLoading: false,
    }));
  };

  // Error dismissal
  const handleDismissError = () => {
    setState((prev) => ({ ...prev, error: null }));
  };

  return (
    <MainLayout>
      <Container size="full">
        <Stack spacing={6}>
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <PageHeader title="User Management" />
            <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-end sm:gap-4">
              {/* Create User — + Add on mobile, full label on sm+ */}
              <button
                type="button"
                onClick={handleCreateUser}
                className="shrink-0 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:hidden"
              >
                + Add
              </button>
              <div className="hidden sm:block">
                <Button
                  variant="primary"
                  onClick={handleCreateUser}
                  leftIcon={
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  }
                >
                  Create User
                </Button>
              </div>

              {/* Mobile-only controls: sort + filter */}
              <div className="flex flex-1 items-center justify-end gap-2 sm:hidden">
                {/* Sort selector — mobile only */}
                <div className="relative flex-1">
                  <select
                    value={`${state.sortField}-${state.sortDirection}`}
                    onChange={(e) => {
                      const [field, dir] = e.target.value.split('-') as [SortField, 'asc' | 'desc'];
                      setState((prev) => ({ ...prev, sortField: field, sortDirection: dir }));
                    }}
                    className="w-full appearance-none rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-7 text-sm text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-dark-600 dark:bg-dark-800 dark:text-gray-300"
                    aria-label="Sort users"
                  >
                    <option value="name-asc">Name A–Z</option>
                    <option value="name-desc">Name Z–A</option>
                    <option value="email-asc">Email A–Z</option>
                    <option value="status-asc">Status</option>
                    <option value="companies-desc">Most companies</option>
                    <option value="groups-desc">Most groups</option>
                    <option value="lastLoginAt-desc">Last login ↓</option>
                    <option value="lastLoginAt-asc">Last login ↑</option>
                    <option value="id-desc">Newest first</option>
                    <option value="id-asc">Oldest first</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                    <svg
                      className="h-3.5 w-3.5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>

                {/* Filter icon */}
                <button
                  type="button"
                  onClick={() => setIsFilterOpen(true)}
                  className="relative rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-100 dark:border-dark-600 dark:text-gray-300 dark:hover:bg-dark-700"
                  aria-label="Open filters"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z"
                    />
                  </svg>
                  {activeFilterCount > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary-600 text-[10px] font-bold text-white">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Error Alert */}
          {state.error && (
            <Alert variant="error" onClose={handleDismissError}>
              {state.error}
            </Alert>
          )}

          {/* Filters — hidden on mobile (shown in drawer instead) */}
          <div className="hidden md:block">
            {state.isMetadataLoading ? (
              <div className="rounded-lg border border-gray-200 bg-white shadow-md dark:border-dark-700 dark:bg-dark-800">
                <div className="px-6 pb-4 pt-6">
                  <Skeleton className="h-6 w-16" />
                </div>
                <div className="px-6 pb-6">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                    {Array.from({ length: 4 }).map((_, index) => (
                      <div key={`filter-skeleton-${index}`}>
                        <Skeleton className="mb-2 h-4 w-24" />
                        <Skeleton className="h-10 w-full" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <UserFilters
                filters={state.filters}
                onFiltersChange={handleFiltersChange}
                companies={state.companies}
                userGroups={state.userGroups}
                onClearFilters={handleClearFilters}
              />
            )}
          </div>

          {/* Users List */}
          <UserTable
            users={paginatedUsers}
            onEdit={handleEditUser}
            onDelete={handleDeleteUser}
            onResendInvite={handleResendInvite}
            isLoading={state.isUsersLoading}
            sortField={state.sortField}
            sortDirection={state.sortDirection}
            onSort={handleSort}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="order-2 text-center text-sm text-gray-700 dark:text-gray-300 sm:order-1 sm:text-left">
                Showing {(state.currentPage - 1) * state.pageSize + 1} to{' '}
                {Math.min(state.currentPage * state.pageSize, totalCount)} of {totalCount} users
              </div>

              <div className="order-1 flex flex-wrap items-center justify-center gap-2 sm:order-2 sm:justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(state.currentPage - 1)}
                  disabled={state.currentPage <= 1}
                >
                  Previous
                </Button>

                {paginationItems.map((item) => {
                  if (typeof item === 'string') {
                    return (
                      <span
                        key={item}
                        className="px-2 text-sm text-gray-500 dark:text-gray-400"
                        aria-hidden="true"
                      >
                        ...
                      </span>
                    );
                  }

                  return (
                    <Button
                      key={item}
                      variant={item === state.currentPage ? 'primary' : 'outline'}
                      size="sm"
                      onClick={() => handlePageChange(item)}
                    >
                      {item}
                    </Button>
                  );
                })}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(state.currentPage + 1)}
                  disabled={state.currentPage >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Stack>
      </Container>

      {/* User Form Modal */}
      {dialogs.showForm && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="user-form-title"
        >
          <div
            className="fixed inset-0 bg-gray-900/50 transition-opacity dark:bg-black/60"
            aria-hidden="true"
          />
          <div className="relative z-10 flex max-h-[min(92dvh,880px)] min-h-0 w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl border border-gray-200 bg-white shadow-2xl dark:border-dark-700 dark:bg-dark-800 sm:rounded-xl">
            <UserForm
              user={dialogs.editingUser}
              companies={state.companies}
              userGroups={state.userGroups}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={dialogs.showDeleteConfirm}
        title="Delete User"
        message={`Are you sure you want to delete "${dialogs.deletingUser?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDialog}
        variant="danger"
        isLoading={dialogs.formLoading}
      />

      {/* Mobile filter drawer backdrop */}
      {isFilterOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setIsFilterOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile filter drawer panel — slides in from the right */}
      <div
        className={`fixed inset-y-0 right-0 z-50 flex w-80 flex-col bg-white shadow-xl transition-transform duration-300 ease-in-out dark:bg-dark-800 md:hidden ${
          isFilterOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-dark-600">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Filters</h2>
            {activeFilterCount > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-600 px-1 text-xs font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setIsFilterOpen(false)}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-700 dark:hover:text-gray-300"
            aria-label="Close filters"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Drawer content */}
        <div className="flex-1 overflow-y-auto p-4">
          {state.isMetadataLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={`drawer-filter-skeleton-${i}`}>
                  <Skeleton className="mb-2 h-4 w-24" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </div>
          ) : (
            <UserFilters
              filters={state.filters}
              onFiltersChange={handleFiltersChange}
              companies={state.companies}
              userGroups={state.userGroups}
              onClearFilters={handleClearFilters}
              inDrawer
            />
          )}
        </div>

        {/* Drawer footer */}
        <div className="flex gap-3 border-t border-gray-200 p-4 dark:border-dark-600">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => {
              handleClearFilters();
              setIsFilterOpen(false);
            }}
          >
            Clear
          </Button>
          <Button variant="primary" className="flex-1" onClick={() => setIsFilterOpen(false)}>
            Filter
          </Button>
        </div>
      </div>
    </MainLayout>
  );
};
